import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  ArrowLeft,
  Calendar,
  Clock,
  ExternalLink,
  Share2,
  Check,
  Twitter,
  Linkedin,
  ChevronRight,
  BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getBlogBySlug,
  getAllArchivedBlogs,
  getSlugFromArticle,
  calculateReadTime,
  formatMediumArticleHtml
} from '../utils/blogUtils';

const BlogPostDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [copied, setCopied] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const found = getBlogBySlug(slug);
    if (found) {
      setArticle(found);
      document.title = `${found.title} | Team India FGC Blog`;
    } else {
      setArticle(null);
    }
  }, [slug]);

  // Track scroll progress for reading progress bar
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (totalHeight > 0) {
        const currentProgress = (window.scrollY / totalHeight) * 100;
        setScrollProgress(Math.min(100, Math.max(0, currentProgress)));
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!article) {
    return (
      <div className="min-h-screen bg-cream flex flex-col justify-between font-sans">
        <Header />
        <main className="py-24 px-6 text-center max-w-xl mx-auto">
          <div className="w-16 h-16 bg-dwarfpony/10 text-dwarfpony rounded-2xl flex items-center justify-center mx-auto mb-6">
            <BookOpen size={32} />
          </div>
          <h1 className="text-3xl font-extrabold text-midnight mb-4">Article Not Found</h1>
          <p className="text-heavyrain mb-8">
            The blog article you are looking for might have been moved or removed.
          </p>
          <Button
            onClick={() => navigate('/blogs')}
            className="bg-midnight hover:bg-midnight-light text-white rounded-full px-8 py-3 text-sm font-bold shadow-lg"
          >
            ← Back to All Articles
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  const allBlogs = getAllArchivedBlogs();
  const relatedArticles = allBlogs
    .filter(b => getSlugFromArticle(b) !== getSlugFromArticle(article))
    .slice(0, 3);

  const formattedHtml = formatMediumArticleHtml(article.fullContent || `<p>${article.excerpt}</p>`);
  const readTime = calculateReadTime(article.fullContent || article.excerpt);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success("Article link copied to clipboard!");
    setTimeout(() => setCopied(false), 2500);
  };

  const shareOnTwitter = () => {
    const text = encodeURIComponent(`Read "${article.title}" by Team India FGC`);
    const url = encodeURIComponent(window.location.href);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    const url = encodeURIComponent(window.location.href);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col justify-between font-sans selection:bg-dwarfpony/20 selection:text-midnight">
      {/* Scroll Progress Bar at the top of window */}
      <div className="fixed top-0 left-0 right-0 h-1.5 bg-pragmatic/20 z-50">
        <div
          className="h-full bg-dwarfpony transition-all duration-150 ease-out"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      <Header />

      <main className="pt-24 pb-20">
        {/* Back Navigation Bar */}
        <div className="max-w-4xl mx-auto px-6 mb-8">
          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-heavyrain hover:text-midnight transition-colors group py-2"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform text-dwarfpony" />
            <span>Back to All Articles</span>
          </Link>
        </div>

        {/* Article Header Section */}
        <header className="max-w-4xl mx-auto px-6 mb-10">
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <Badge className="bg-midnight text-white text-xs font-extrabold uppercase px-3 py-1 rounded-full border-none">
              {article.sourceName}
            </Badge>
            {article.categories.map((cat, idx) => (
              <Badge key={idx} className="bg-dwarfpony/10 text-dwarfpony border border-dwarfpony/20 text-xs font-extrabold uppercase px-3 py-1 rounded-full">
                {cat}
              </Badge>
            ))}
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-midnight mb-6 leading-[1.15] tracking-tight">
            {article.title}
          </h1>

          {/* Author Byline & Meta Info */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-b border-pragmatic/20 pb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-midnight text-white font-extrabold flex items-center justify-center text-sm shadow-md border-2 border-white">
                FGC
              </div>
              <div>
                <div className="font-extrabold text-midnight text-sm sm:text-base flex items-center gap-2">
                  <span>{article.author}</span>
                  <span className="text-[10px] bg-richolive/15 text-richolive font-bold px-2 py-0.5 rounded-full uppercase">Official</span>
                </div>
                <div className="flex items-center gap-3 text-xs font-semibold text-heavyrain mt-0.5">
                  <span className="flex items-center gap-1">
                    <Calendar size={13} className="text-dwarfpony" />
                    {new Date(article.pubDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Clock size={13} className="text-richolive" />
                    {readTime}
                  </span>
                </div>
              </div>
            </div>

            {/* Share Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleCopyLink}
                className="p-2.5 rounded-full bg-cream-dark border border-pragmatic/30 text-heavyrain hover:text-midnight hover:border-midnight/40 transition-colors shadow-xs"
                title="Copy Article Link"
              >
                {copied ? <Check size={16} className="text-green-600" /> : <Share2 size={16} />}
              </button>
              <button
                onClick={shareOnTwitter}
                className="p-2.5 rounded-full bg-cream-dark border border-pragmatic/30 text-heavyrain hover:text-[#1DA1F2] hover:border-[#1DA1F2]/40 transition-colors shadow-xs"
                title="Share on X (Twitter)"
              >
                <Twitter size={16} />
              </button>
              <button
                onClick={shareOnLinkedIn}
                className="p-2.5 rounded-full bg-cream-dark border border-pragmatic/30 text-heavyrain hover:text-[#0A66C2] hover:border-[#0A66C2]/40 transition-colors shadow-xs"
                title="Share on LinkedIn"
              >
                <Linkedin size={16} />
              </button>
              {article.link && (
                <a
                  href={article.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 rounded-full bg-cream-dark border border-pragmatic/30 text-heavyrain hover:text-dwarfpony hover:border-dwarfpony/40 transition-colors shadow-xs ml-1"
                  title="View Original on Medium"
                >
                  <ExternalLink size={16} />
                </a>
              )}
            </div>
          </div>
        </header>

        {/* Hero Cover Image */}
        {article.thumbnail && (
          <div className="max-w-4xl mx-auto px-6 mb-12">
            <div className="relative aspect-[16/9] max-h-[500px] rounded-3xl overflow-hidden shadow-xl bg-midnight border border-pragmatic/20">
              <img
                src={article.thumbnail}
                alt={article.title}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = '/assets/images/2026.jpg'; }}
              />
            </div>
          </div>
        )}

        {/* Medium-Classy Main Article Reading Body */}
        <article className="max-w-3xl mx-auto px-6">
          <div
            className="prose prose-lg prose-stone max-w-none text-deepwalnut font-serif"
            dangerouslySetInnerHTML={{ __html: formattedHtml }}
          />

          {/* Original Source Reference Box */}
          <div className="mt-16 p-6 sm:p-8 rounded-3xl bg-midnight text-white shadow-xl relative overflow-hidden flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="relative z-10">
              <div className="text-xs font-bold text-dwarfpony uppercase tracking-wider mb-1">Originally Published on Medium</div>
              <h3 className="text-xl font-extrabold text-white mb-2">Enjoyed this story?</h3>
              <p className="text-sm text-heavyrain max-w-md">
                Follow Team India FGC on Medium to stay updated with robotics, STEM outreach, and international competition updates.
              </p>
            </div>
            {article.link && (
              <a
                href={article.link}
                target="_blank"
                rel="noopener noreferrer"
                className="relative z-10 shrink-0 inline-flex items-center gap-2 bg-dwarfpony hover:bg-dwarfpony-dark text-white font-extrabold text-xs uppercase tracking-wider px-6 py-3.5 rounded-full shadow-lg transition-transform hover:scale-105"
              >
                <span>Read on Medium</span>
                <ExternalLink size={14} />
              </a>
            )}
          </div>
        </article>

        {/* Related Stories Section */}
        {relatedArticles.length > 0 && (
          <section className="max-w-5xl mx-auto px-6 mt-20 pt-16 border-t border-pragmatic/20">
            <div className="flex items-center justify-between mb-10">
              <div>
                <span className="text-xs font-extrabold text-dwarfpony uppercase tracking-wider">Keep Reading</span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-midnight">More Stories from Team India</h2>
              </div>
              <Link
                to="/blogs"
                className="text-xs font-extrabold text-dwarfpony hover:text-dwarfpony-dark flex items-center gap-1 transition-colors"
              >
                <span>View All Articles</span>
                <ChevronRight size={14} />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedArticles.map((relItem, idx) => {
                const relSlug = getSlugFromArticle(relItem);
                return (
                  <Link
                    key={idx}
                    to={`/blogs/${relSlug}`}
                    className="group bg-cream-dark border border-pragmatic/20 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col justify-between hover:-translate-y-1"
                  >
                    <div>
                      <div className="relative aspect-video overflow-hidden bg-midnight">
                        <img
                          src={relItem.thumbnail || '/assets/images/2026.jpg'}
                          alt={relItem.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <div className="text-[11px] font-semibold text-heavyrain mb-2 flex items-center gap-1">
                          <Calendar size={12} className="text-dwarfpony" />
                          {new Date(relItem.pubDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                        <h4 className="font-bold text-midnight text-sm line-clamp-2 leading-snug group-hover:text-dwarfpony transition-colors">
                          {relItem.title}
                        </h4>
                      </div>
                    </div>
                    <div className="px-5 pb-5 pt-0 flex items-center gap-1 text-xs font-bold text-dwarfpony group-hover:translate-x-1 transition-transform">
                      <span>Read Story</span>
                      <ChevronRight size={14} />
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default BlogPostDetailPage;
